/**
 * Il contesto che parte con ogni domanda, uguale nel pannello (V58) e nella
 * vista a tutta pagina (V59), e il modo di ricominciare da capo.
 *
 * Fuori da `/assistente` squadra e vista si ricavano dal percorso. Sotto
 * `/assistente` il percorso non porta una squadra: vale quella in cui la
 * conversazione e nata e, per una conversazione nuova, quella del selettore
 * del menu. Una conversazione riletta dal server senza squadra ha `squadra`
 * null, e resta senza; una nuova ce l'ha indefinita.
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { VISTE_ASSISTENTE, contestoDaPercorso } from '@calcio/shared';
import { useContesto } from '../navigazione/contesto.js';
import { squadraDiRiferimento } from '../navigazione/ultimaSquadra.js';
import { useAssistente } from './contesto.js';
import type { ContestoDomanda } from './Dialogo.js';

export function useContestoDomanda(): {
  contesto: ContestoDomanda;
  /** "Prima Squadra · 2026/27", o che non ce n'e una. */
  nomeSquadra: string;
  /** Il titolo della vista aperta, se l'assistente la conosce. */
  titoloVista?: string;
} {
  const { squadre } = useContesto();
  const { conversazione } = useAssistente();
  const { pathname } = useLocation();

  const dalPercorso = contestoDaPercorso(pathname);
  const squadraStagioneId =
    dalPercorso.squadraStagioneId ??
    (conversazione.squadra === undefined
      ? squadraDiRiferimento(squadre ?? [])?.id
      : conversazione.squadra?.id);
  const contesto: ContestoDomanda = {
    ...dalPercorso,
    ...(squadraStagioneId && { squadraStagioneId }),
  };

  const squadra = squadre?.find((s) => s.id === squadraStagioneId);
  const nomeSquadra = squadra
    ? `${squadra.squadra} · ${squadra.stagione}`
    : conversazione.squadra
      ? `${conversazione.squadra.nome} · ${conversazione.squadra.stagione}`
      : 'Nessuna squadra aperta';
  const vista = contesto.vista ? VISTE_ASSISTENTE[contesto.vista.chiave] : undefined;

  return { contesto, nomeSquadra, ...(vista && { titoloVista: vista.titolo }) };
}

/**
 * «Nuova»: ricomincia da capo. Sulla vista a tutta pagina porta anche alla
 * pagina della conversazione nuova, che altrimenti mostrerebbe ancora il
 * titolo di quella lasciata.
 */
export function useNuovaConversazione(): () => void {
  const { dimentica } = useAssistente();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return () => {
    dimentica();
    if (pathname.startsWith('/assistente/')) void navigate('/assistente/nuova');
  };
}
